/**
 * Which tasks a report Tasks section shows.
 *
 * The section reads the same org and incident task lists the Hub Tasks tab
 * reads, then narrows them here. Snapshots run the same rules on the server, so
 * keep this pure: no hooks, no fetching.
 */

import type { MultiPolygon, Polygon } from 'geojson';
import { isActionInArea } from 'components/IAP/utils/actionSpatialFilter';

export type TaskSectionScope = 'follow' | 'org' | 'incident' | 'both';
export type TaskSectionArea = 'report' | 'aor' | 'none';
export type TaskTable = 'org' | 'incident';

export type TaskSectionFilter = {
  /** Follow: incident tasks on an incident report, org tasks otherwise. */
  scope: TaskSectionScope;
  /**
   * Report: inside the report area or assigned to the report's area of
   * responsibility (or one below it). aor: the area of responsibility only.
   */
  area: TaskSectionArea;
  statuses: string[];
  priorities: string[];
  /** Assignee ids or POC user ids. */
  assignees: string[];
  /** Tasks this section generated or someone pinned always show. */
  includeLinked: boolean;
};

export const DEFAULT_TASK_SECTION_FILTER: TaskSectionFilter = {
  scope: 'follow',
  area: 'report',
  statuses: [],
  priorities: [],
  assignees: [],
  includeLinked: true,
};

/** What a "Tasks in area" section showed before it had settings. */
export const LEGACY_TASK_SECTION_FILTER: TaskSectionFilter = {
  ...DEFAULT_TASK_SECTION_FILTER,
  scope: 'both',
};

/**
 * What an OPORD Tasks section showed before it had settings: every task in its
 * table.
 */
export const LEGACY_OPORD_TASK_SECTION_FILTER: TaskSectionFilter = {
  ...DEFAULT_TASK_SECTION_FILTER,
  scope: 'follow',
  area: 'none',
};

const SCOPES: readonly TaskSectionScope[] = [
  'follow',
  'org',
  'incident',
  'both',
];
const AREAS: readonly TaskSectionArea[] = ['report', 'aor', 'none'];

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && !!item)
    : [];
}

export function readTaskSectionFilter(
  structured: unknown,
  options: { isOpord?: boolean } = {}
): TaskSectionFilter {
  const fallback = options.isOpord
    ? LEGACY_OPORD_TASK_SECTION_FILTER
    : LEGACY_TASK_SECTION_FILTER;
  const raw =
    structured && typeof structured === 'object' && !Array.isArray(structured)
      ? (structured as { task_filter?: unknown }).task_filter
      : undefined;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fallback;
  const row = raw as Record<string, unknown>;
  return {
    scope: SCOPES.includes(row.scope as TaskSectionScope)
      ? (row.scope as TaskSectionScope)
      : fallback.scope,
    area: AREAS.includes(row.area as TaskSectionArea)
      ? (row.area as TaskSectionArea)
      : fallback.area,
    statuses: stringList(row.statuses),
    priorities: stringList(row.priorities),
    assignees: stringList(row.assignees),
    includeLinked: row.includeLinked !== false,
  };
}

/** The task tables a section reads, in display order. */
export function resolveTaskTables(
  scope: TaskSectionScope,
  incidentId: string | null | undefined
): TaskTable[] {
  switch (scope) {
    case 'org':
      return ['org'];
    case 'incident':
      return incidentId ? ['incident'] : [];
    case 'both':
      return incidentId ? ['incident', 'org'] : ['org'];
    case 'follow':
    default:
      return incidentId ? ['incident'] : ['org'];
  }
}

export type SectionTaskLike = {
  action_id?: string | null;
  aor_id?: string | null;
  status?: string | null;
  priority?: string | null;
  assignee_id?: string | null;
  poc?: string | null;
  archived?: boolean | null;
};

export type SelectSectionTasksParams<T extends SectionTaskLike> = {
  org: readonly T[];
  incident: readonly T[];
  filter: TaskSectionFilter;
  incidentId?: string | null;
  /** The report area. Without one, the area rule falls back to the AOR alone. */
  areaGeometry?: Polygon | MultiPolygon | null;
  /** The report's area of responsibility and every one below it. */
  aorIds?: ReadonlySet<string> | null;
  linkedActionIds?: ReadonlySet<string> | null;
};

function matchesArea<T extends SectionTaskLike>(
  task: T,
  area: TaskSectionArea,
  geometry: Polygon | MultiPolygon | null | undefined,
  aorIds: ReadonlySet<string> | null | undefined
): boolean {
  if (area === 'none') return true;
  const hasAor = !!aorIds && aorIds.size > 0;
  const inAor = hasAor && !!task.aor_id && aorIds.has(task.aor_id);
  if (area === 'aor') return hasAor ? inAor : true;
  if (!geometry) return hasAor ? inAor : true;
  return inAor || isActionInArea(task, geometry);
}

function matchesList(value: string | null | undefined, allowed: string[]) {
  return allowed.length === 0 || (!!value && allowed.includes(value));
}

export function matchesTaskSectionFilter<T extends SectionTaskLike>(
  task: T,
  params: Omit<SelectSectionTasksParams<T>, 'org' | 'incident' | 'incidentId'>
): boolean {
  if (task.archived) return false;
  const { filter } = params;
  if (
    filter.includeLinked &&
    task.action_id &&
    params.linkedActionIds?.has(task.action_id)
  ) {
    return true;
  }
  if (!matchesArea(task, filter.area, params.areaGeometry, params.aorIds)) {
    return false;
  }
  if (!matchesList(task.status, filter.statuses)) return false;
  if (!matchesList(task.priority, filter.priorities)) return false;
  if (
    filter.assignees.length > 0 &&
    !filter.assignees.some((id) => id === task.assignee_id || id === task.poc)
  ) {
    return false;
  }
  return true;
}

/**
 * The org and incident tasks a section shows. A table the scope excludes comes
 * back empty.
 */
export function selectSectionTasks<T extends SectionTaskLike>(
  params: SelectSectionTasksParams<T>
): Record<TaskTable, T[]> {
  const tables = resolveTaskTables(params.filter.scope, params.incidentId);
  const pick = (table: TaskTable, rows: readonly T[]) =>
    tables.includes(table)
      ? rows.filter((task) => matchesTaskSectionFilter(task, params))
      : [];
  return {
    org: pick('org', params.org),
    incident: pick('incident', params.incident),
  };
}
